import { useState, useEffect } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import NewQBCustomerModal from "./NewQBCustomerModal";
import QBLinkModal from "./QBLinkModal";
import { qbMatchScore } from "./qbMatchScore";
import Icon from "../ui/Icon";
import MiniSpinner from "../ui/MiniSpinner";
import { BRAND, THEMES } from "../../config/constants";
import { db } from "../../firebase";
import useMobile from "../../hooks/useMobile";
import { qbCall } from "../../utils/api";
import { tp, btnP, btnS } from "../../utils/styles";

export default function QuickBooksPage({ clients, theme, mobile }) {
  const t = THEMES[theme];
  const _isMobile = useMobile();
  const isMobile = mobile || _isMobile;
  const [qbStatus, setQbStatus] = useState(null);
  const [qbCustomers, setQbCustomers] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [connectingOAuth, setConnectingOAuth] = useState(false);
  const [search, setSearch] = useState("");
  const [filterTab, setFilterTab] = useState("all");
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [linkModal, setLinkModal] = useState(null);
  const [prefillClient, setPrefillClient] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    qbCall("qbGetStatus").then(res => setQbStatus(res)).catch(() => setQbStatus({ connected: false }));
    const unsub = onSnapshot(collection(db, "qb_customers"), snap => setQbCustomers(snap.docs.map(d => ({ ...d.data() }))));
    return unsub;
  }, []);

  const handleConnect = async () => {
    setConnectingOAuth(true); setError("");
    try {
      const res = await qbCall("qbGetAuthUrl");
      window.open(res.url, "_blank", "width=600,height=700");
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const s = await qbCall("qbGetStatus");
          if (s.connected) { setQbStatus(s); clearInterval(poll); setConnectingOAuth(false); setSuccess("QuickBooks connected!"); }
        } catch {}
        if (attempts > 60) { clearInterval(poll); setConnectingOAuth(false); }
      }, 3000);
    } catch (e) { setError(e.message); setConnectingOAuth(false); }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect QuickBooks?")) return;
    await qbCall("qbDisconnect");
    setQbStatus({ connected: false });
  };

  const handleSync = async () => {
    setSyncing(true); setError(""); setSuccess("");
    try {
      const res = await qbCall("qbFetchCustomers");
      setSuccess(`Synced ${res.count} QuickBooks customers.`);
    } catch (e) { setError(e.message); }
    setSyncing(false);
  };

  const handleLink = async (crmClientId, qbCustomerId, qbCustomerName) => {
    try {
      await qbCall("qbLinkClient", { crmClientId, qbCustomerId, qbCustomerName });
      setSuccess("Linked successfully.");
    } catch (e) { setError(e.message); }
  };

  const handleUnlink = async (crmClientId, qbCustomerId) => {
    try {
      await qbCall("qbUnlinkClient", { crmClientId, qbCustomerId });
      setSuccess("Unlinked.");
    } catch (e) { setError(e.message); }
  };

  const filteredClients = clients.filter(c => {
    const ms = !search || c.name?.toLowerCase().includes(search.toLowerCase()) || c.email?.toLowerCase().includes(search.toLowerCase());
    const linked = (c.qbCustomerIds?.length > 0) || !!c.qbCustomerId;
    const tab = filterTab === "all" || (filterTab === "linked" && linked) || (filterTab === "unlinked" && !linked);
    return ms && tab;
  });

  const matchedCount = clients.filter(c => (c.qbCustomerIds?.length > 0) || !!c.qbCustomerId).length;

  if (showNewCustomer) return (
    <NewQBCustomerModal
      onClose={() => { setShowNewCustomer(false); setPrefillClient(null); }}
      onCreated={async (newCust) => {
        setShowNewCustomer(false);
        if (prefillClient) {
          await handleLink(prefillClient.id, newCust.Id || newCust.id, newCust.DisplayName || newCust.displayName);
          setPrefillClient(null);
        }
        await handleSync();
      }}
      theme={theme}
      prefill={prefillClient}
    />
  );

  if (linkModal) return (
    <QBLinkModal
      crmClient={linkModal}
      qbCustomers={qbCustomers}
      onLink={handleLink}
      onClose={() => setLinkModal(null)}
      theme={theme}
    />
  );

  return (
    <div>

      {/* Connection Card */}
      <div style={{ background: t.surface, border: `1px solid ${qbStatus?.connected ? BRAND.green + "40" : t.border}`, borderRadius: 16, padding: 20, marginBottom: 20, display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ width: 52, height: 52, borderRadius: 14, background: qbStatus?.connected ? BRAND.green + "18" : t.bg, border: `2px solid ${qbStatus?.connected ? BRAND.green + "50" : t.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <span style={{ fontSize: 20, fontWeight: 900, color: qbStatus?.connected ? BRAND.green : t.textMuted, letterSpacing: "-1px" }}>QB</span>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: t.text, marginBottom: 3 }}>QuickBooks Online</div>
          {qbStatus === null
            ? <div style={{ fontSize: 12, color: t.textMuted }}>Checking connection...</div>
            : qbStatus?.connected
              ? <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12, color: BRAND.green, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}><Icon name="check" size={12} color={BRAND.green} />Connected</span>
                  <span style={{ fontSize: 11, color: t.textMuted }}>Realm: {qbStatus.realmId}</span>
                  <span style={{ fontSize: 11, color: t.textMuted }}>{qbCustomers.length} customers synced</span>
                </div>
              : <div style={{ fontSize: 12, color: "#f87171", fontWeight: 600 }}>⚠ Not connected</div>
          }
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          {qbStatus?.connected ? (
            <>
              <button onClick={handleSync} disabled={syncing} style={{ ...btnS(t), display: "flex", alignItems: "center", gap: 6 }}>
                {syncing ? <MiniSpinner size={14} color={BRAND.blue}/> : <Icon name="refresh" size={14} color={BRAND.blue} />}
                {syncing ? <><MiniSpinner size={13} color="#fff"/>Syncing...</> : "Sync Customers"}
              </button>
              <button onClick={handleDisconnect} style={{ ...btnS(t), color: "#f87171", borderColor: "#f8717140" }}>Disconnect</button>
            </>
          ) : (
            <button onClick={handleConnect} disabled={connectingOAuth} style={btnP}>
              {connectingOAuth ? <><MiniSpinner size={14} color="#fff"/>Opening QuickBooks...</> : <><Icon name="plug" size={14} color="#fff"/>Connect QuickBooks</>}
            </button>
          )}
        </div>
      </div>

      {error && <div style={{ background: "#2a0e0e", border: "1px solid #5a1010", borderRadius: 10, padding: "12px 16px", color: "#f87171", fontSize: 13, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}><Icon name="warning" size={14} color="#f87171" />{error}<button onClick={() => setError("")} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#f87171" }}>✕</button></div>}
      {success && <div style={{ background: theme === "dark" ? "#0e2818" : "#edfaf0", border: `1px solid ${BRAND.green}40`, borderRadius: 10, padding: "12px 16px", color: BRAND.green, fontSize: 13, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}><Icon name="check" size={14} color={BRAND.green} />{success}<button onClick={() => setSuccess("")} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: BRAND.green }}>✕</button></div>}

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${isMobile?2:4},1fr)`, gap: isMobile?10:14, marginBottom: 20 }}>
        {[{ l: "CRM Clients", v: clients.length, c: BRAND.blue }, { l: "QB Customers", v: qbCustomers.length, c: "#a78bfa" }, { l: "Linked", v: matchedCount, c: BRAND.green }, { l: "Unlinked", v: clients.length - matchedCount, c: "#f59e0b" }].map(s => (
          <div key={s.l} style={{ background: t.surface, border: `1px solid ${s.c}30`, borderRadius: 14, padding: isMobile?"12px 14px":"16px 20px", borderTop: `3px solid ${s.c}` }}>
            <div style={{ fontSize: isMobile?22:30, fontWeight: 800, color: s.c, letterSpacing: "-1px", marginBottom: 2 }}>{s.v}</div>
            <div style={{ fontSize: 11, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.08em" }}>{s.l}</div>
          </div>
        ))}
      </div>

      {/* Matching Table */}
      <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 16, padding: isMobile?14:20 }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 14, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ fontWeight: 800, fontSize: 14, color: t.text }}>Client → QB Matching</div>
          <div style={{ display: "flex", alignItems: "center", background: t.inputBg, border: `1px solid ${t.border}`, borderRadius: 9, padding: "7px 12px", gap: 8, flex: 1, marginLeft: "auto" }}>
            <Icon name="search" size={14} color={t.textMuted} />
            <input style={{ background: "transparent", border: "none", outline: "none", color: t.text, fontSize: 13, width: "100%", fontFamily: "'Outfit',sans-serif" }} placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, marginBottom: 12, overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          {[["all", "All"], ["linked", "Linked"], ["unlinked", "Unlinked"]].map(([val, label]) => (
            <button key={val} onClick={() => setFilterTab(val)} style={{ padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: `1px solid ${filterTab === val ? BRAND.blue + "50" : t.border}`, background: filterTab === val ? BRAND.blue + "15" : t.bg, color: filterTab === val ? BRAND.blue : t.textSub, fontFamily: "'Outfit',sans-serif", whiteSpace: "nowrap", flexShrink: 0 }}>{label}</button>
          ))}
          <button onClick={() => { setPrefillClient(null); setShowNewCustomer(true); }} style={{ ...btnP, background: `linear-gradient(135deg,${BRAND.green},#6fa830)`, display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap", flexShrink: 0 }}>
            <Icon name="plus" size={14} color="#fff" />{isMobile?"New QB":"New QB Customer"}
          </button>
        </div>

        <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>{["CRM Client", "Email", "Type", "QB Status", "QB Customer", "Actions"].map(h => (
              <th key={h} style={{ textAlign: "left", padding: "10px 12px", color: t.textMuted, fontWeight: 600, fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.08em", borderBottom: `1px solid ${t.border}` }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {filteredClients.map(client => {
              const qbLinks = client.qbCustomerIds || (client.qbCustomerId ? [{ qbCustomerId: client.qbCustomerId, qbCustomerName: client.qbCustomerName || "" }] : []);
              const isLinked = qbLinks.length > 0;
              const suggestions = !isLinked ? [...qbCustomers].map(q => ({ ...q, score: qbMatchScore(q, client) })).filter(q => q.score >= 60).sort((a, b) => b.score - a.score) : [];
              const top = suggestions[0];
              return (
                <tr key={client.id}>
                  <td style={{ padding: "11px 12px", borderBottom: `1px solid ${t.borderLight}` }}>
                    <div style={{ fontWeight: 700, color: t.text }}>{client.name}</div>
                    {client.spouseName && <div style={{ fontSize: 11, color: t.textMuted }}>& {client.spouseName}</div>}
                    {(client.businesses?.length||0)>0&&<div style={{fontSize:11,color:t.textMuted}}>{client.businesses.length} biz</div>}
                  </td>
                  <td style={{ padding: "11px 12px", borderBottom: `1px solid ${t.borderLight}` }}><div style={{ fontSize: 12, color: t.textSub }}>{client.email || "—"}</div></td>
                  <td style={{ padding: "11px 12px", borderBottom: `1px solid ${t.borderLight}` }}><span style={tp(client.type, theme)}>{client.type}</span></td>
                  <td style={{ padding: "11px 12px", borderBottom: `1px solid ${t.borderLight}` }}>
                    {isLinked
                      ? <span style={{ fontSize: 11, fontWeight: 700, color: BRAND.green, background: BRAND.green + "18", padding: "2px 9px", borderRadius: 10, border: `1px solid ${BRAND.green}30` }}>✓ {qbLinks.length} Linked</span>
                      : <span style={{ fontSize: 11, fontWeight: 700, color: "#f59e0b", background: "#f59e0b18", padding: "2px 9px", borderRadius: 10, border: "1px solid #f59e0b30" }}>Not Linked</span>}
                  </td>
                  <td style={{ padding: "11px 12px", borderBottom: `1px solid ${t.borderLight}` }}>
                    {isLinked ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {qbLinks.map(link => {
                          const qbMatch = qbCustomers.find(q => q.qbId === link.qbCustomerId);
                          return (
                            <div key={link.qbCustomerId} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 12.5, fontWeight: 700, color: t.text }}>{link.qbCustomerName || qbMatch?.displayName || link.qbCustomerId}</div>
                                {qbMatch?.email && <div style={{ fontSize: 10.5, color: t.textMuted }}>{qbMatch.email}</div>}
                              </div>
                              {qbMatch?.balance > 0 && <span style={{ fontSize: 10.5, color: "#f87171", fontWeight: 600 }}>${qbMatch.balance.toFixed(2)}</span>}
                              <button onClick={() => handleUnlink(client.id, link.qbCustomerId)} style={{ padding: "2px 6px", borderRadius: 5, background: "transparent", border: "1px solid #f8717130", cursor: "pointer", color: "#f87171", fontSize: 10, fontFamily: "'Outfit',sans-serif" }}>✕</button>
                            </div>
                          );
                        })}
                      </div>
                    ) : top ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ fontSize: 11.5, color: t.textSub }}>{top.displayName}</div>
                        <span style={{ fontSize: 10, color: BRAND.blue, background: BRAND.blue + "18", padding: "1px 6px", borderRadius: 8, fontWeight: 700 }}>Suggested</span>
                      </div>
                    ) : <span style={{ fontSize: 11.5, color: t.textMuted }}>—</span>}
                  </td>
                  <td style={{ padding: "11px 12px", borderBottom: `1px solid ${t.borderLight}` }}>
                    <div style={{ display: "flex", gap: 6 }}>
                      {!isLinked && top && (
                        <button onClick={() => handleLink(client.id, top.qbId, top.displayName)} style={{ padding: "5px 10px", borderRadius: 7, background: BRAND.blue + "18", border: `1px solid ${BRAND.blue}40`, cursor: "pointer", color: BRAND.blue, fontSize: 12, fontFamily: "'Outfit',sans-serif", display: "flex", alignItems: "center", gap: 4 }}>
                          <Icon name="check" size={12} color={BRAND.blue} />Auto-Link
                        </button>
                      )}
                      <button onClick={() => setLinkModal(client)} style={{ padding: "5px 10px", borderRadius: 7, background: "transparent", border: `1px solid ${t.border}`, cursor: "pointer", color: t.textSub, fontSize: 12, fontFamily: "'Outfit',sans-serif", display: "flex", alignItems: "center", gap: 4 }}>
                        <Icon name="link" size={12} />{isLinked ? "+ Add" : "Manual"}
                      </button>
                      {!isLinked && (
                        <button onClick={() => { setPrefillClient(client); setShowNewCustomer(true); }} style={{ padding: "5px 10px", borderRadius: 7, background: BRAND.green + "18", border: `1px solid ${BRAND.green}40`, cursor: "pointer", color: BRAND.green, fontSize: 12, fontFamily: "'Outfit',sans-serif", display: "flex", alignItems: "center", gap: 4 }}>
                          <Icon name="plus" size={12} color={BRAND.green} />Create in QB
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        {filteredClients.length === 0 && <div style={{ textAlign: "center", padding: 40, color: t.textMuted }}>No clients match your filters.</div>}
      </div>
    </div>
  );
}

// Client Detail
